import { Button, Form, Modal, notification, Tooltip } from "antd"
import { MailPlus, Share2, Share2Icon } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { sharePreMeetingViaEmail } from "~/api/pre-meeting"
import ButtonPrimary from "~/components/base-button"
import CheckboxSelect from "~/components/checkbox-select"
import useGlobalStore from "~/store/global"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"

import { EVENTS } from "./constants"
const SharePreMeeting = ({
  meetingId,
  accountName,
  isDisabled,
  peopleCount
}: {
  meetingId: string
  isDisabled: boolean
  accountName: string
  peopleCount: number
}) => {
  const { t } = useTranslation()
  const [visible, setVisibility] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const [form] = Form.useForm()
  const onClickHandler = useCallback(async () => {
    track(EVENTS.PRE_MEETING_BRIEF_SHARE_CLICKED, {
      ...getBaseEventProps({ feature: "pre_meeting_brief", accountName }),
      account_name: accountName,
      people_included: peopleCount
    })
    setVisibility(true)
    track(EVENTS.PRE_MEETING_BRIEF_SHARE_MODAL_OPENED, {
      ...getBaseEventProps({ feature: "pre_meeting_brief", accountName }),
      account_name: accountName,
      people_included: peopleCount
    })
  }, [accountName, peopleCount])

  const onClose = useCallback(() => {
    form.resetFields()
    setIsPending(false)
    setVisibility(false)
  }, [form])

  const onSubmit = useCallback(async () => {
    setIsPending(true)

    const values = await form.validateFields()

    if (!values.emails.length) {
      notificationApi.error({
        message: t("pre_meeting_brief.error_generic"),
        description: t("pre_meeting_brief.error_please_select_email")
      })
      setIsPending(false)
      return
    }

    try {
      await sharePreMeetingViaEmail(tokenInfo, values.emails, meetingId)
      notificationApi.success({
        message: t("pre_meeting_brief.notif_share_success")
      })

      track(EVENTS.PRE_MeETING_BRIEF_SHARE_EMAIL_SENT, {
        ...getBaseEventProps({ feature: "pre_meeting_brief", accountName }),
        account_name: accountName,
        people_included: peopleCount,
        recipient_number: values.emails.length,
        email_sent_status: "success"
      })
      onClose()

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      notificationApi.error({
        message: t("pre_meeting_brief.error_generic"),
        description: t("pre_meeting_brief.notif_share_failed")
      })

      track(EVENTS.PRE_MeETING_BRIEF_SHARE_EMAIL_SENT, {
        ...getBaseEventProps({ feature: "pre_meeting_brief", accountName }),
        account_name: accountName,
        people_included: peopleCount,
        recipient_number: values.emails.length,
        email_sent_status: "failed",
        failure_reason: error
      })
    }
    setIsPending(false)
  }, [
    accountName,
    form,
    meetingId,
    notificationApi,
    onClose,
    peopleCount,
    tokenInfo,
    t
  ])

  return (
    <>
      {notificationContextHolder}
      <Tooltip
        title={
          <span className="text-xs">
            {t("pre_meeting_brief.tooltip_share_pre_meeting_brief")}
          </span>
        }
      >
        <button
          className="flex items-center gap-1 text-xs font-medium text-primary dark:text-primary-light"
          onClick={onClickHandler}
          disabled={isDisabled}
        >
          <Share2 size={14} />
          {t("pre_meeting_brief.cta_share_via_email")}
        </button>
      </Tooltip>
      <Modal
        title={
          <p className="text-lg font-semibold dark:text-dark-6 text-dark-2">
            {t("pre_meeting_brief.modal_share_via_email")}
          </p>
        }
        open={!!visible}
        onCancel={onClose}
        width={"55%"}
        centered
        classNames={{
          content: "dark:!bg-dark-2 overflow-y-auto max-h-[calc(100vh-200px)]",
          body: "flex flex-col ",
          header: "flex h-10 dark:!bg-dark-2"
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>
              {t("pre_meeting_brief.cta_cancel")}
            </Button>
            <ButtonPrimary
              className="justify-center flex items-center gap-2 h-8"
              icon={<Share2Icon className="w-3 h-3" />}
              onClick={form.submit}
              loading={isPending}
            >
              {t("pre_meeting_brief.cta_share")}
            </ButtonPrimary>
          </div>
        }
      >
        <div className="flex justify-center h-10">
          <MailPlus
            strokeWidth={0.5}
            className="w-10 h-10 text-dark dark:text-dark-6"
          />
        </div>
        <Form
          className="flex flex-col gap-4 mb-16 h-20"
          form={form}
          onFinish={onSubmit}
          scrollToFirstError
          autoComplete="off"
        >
          <Form.Item
            name="emails"
            label={t("pre_meeting_brief.label_email_ids")}
            rules={[
              {
                required: true,
                message: t("pre_meeting_brief.validation_please_enter_emails")
              }
            ]}
            layout="vertical"
          >
            <SearchCollaborators
              accountName={accountName}
              peopleCount={peopleCount}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

const SearchCollaborators = ({
  accountName,
  peopleCount,
  ...props
}: {
  accountName: string
  peopleCount: number
}) => {
  const [list, setList] = useState<string[]>([])
  const tenant = useGlobalStore((store) => store.tenant)
  const listRef = useRef<string[]>([])
  const { t } = useTranslation()

  useEffect(() => {
    return () => {
      listRef.current = []
    }
  }, [])

  const options = useMemo(() => {
    return list?.map((item: string) => ({
      label: item,
      value: item
    }))
  }, [list])

  const onSearch = useCallback(
    (value: string) => {
      if (
        value.trim().length > 0 &&
        tenant?.allowed_collaborator_domains &&
        tenant?.allowed_collaborator_domains.length > 0
      ) {
        setList(
          listRef.current.concat(
            (tenant?.allowed_collaborator_domains ?? []).map(
              (item) => `${value}@${item}`
            )
          )
        )
        track(EVENTS.PRE_MEETING_BRIEF_EMAIL_RECIPIENT_SELECTED, {
          ...getBaseEventProps({ feature: "pre_meeting_brief", accountName }),
          account_name: accountName,
          people_included: peopleCount
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenant?.allowed_collaborator_domains]
  )

  return (
    <CheckboxSelect
      options={options}
      mode="multiple"
      className="h-20"
      showSearch
      onSearch={onSearch}
      {...props}
      placeholder={t("pre_meeting_brief.placeholder_choose_email_id")}
    />
  )
}
export default SharePreMeeting
